import React, { useEffect } from "react";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { cartOpen, closeCart, cart, removeFromCart, totalPrice, updateQuantity } =
    useCart();
  const navigate = useNavigate();

  console.log(cart);

  useEffect(() => {
  const body = document.body;

  if (cartOpen) {
    // Bloqueamos scroll global
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
  } else {
    // Restauramos cuando se cierra
    body.style.overflow = "";
    body.style.touchAction = "";
  }

  return () => {
    // Fallback por si se desmonta inesperadamente
    body.style.overflow = "";
    body.style.touchAction = "";
  };
}, [cartOpen]);


  const getPackText = (item) => {
    const packLabel = item.pack?.toLowerCase().includes("pack")
      ? item.pack.replace("PACK", "").trim()
      : item.pack || "1";
    const quantity = Number(item.quantity) || 1;
    return `${quantity * parseInt(packLabel || 1)} botellas de ${item.name}`;
  };

  // 👉 Lógica de swipe para cerrar
  const handleDragEnd = (_event, info) => {
    if (info.offset.x > 80) {
      // si arrastra suficiente hacia la derecha, se cierra
      closeCart();
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          {/* Fondo oscuro blur */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
          />

          {/* Drawer principal */}
          <motion.div
            className="fixed right-0 top-0 h-full w-full sm:w-[430px] bg-white z-50 shadow-2xl flex flex-col overflow-y-auto touch-pan-y overflow-y-auto touch-pan-y overscroll-contain"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="x"
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={handleDragEnd}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-xl font-bold">Tu carrito</h2>
              <button
                onClick={closeCart}
                className="text-gray-500 hover:text-black transition"
              >
                <X size={22} />
              </button>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-20 text-gray-500 flex flex-col items-center justify-center gap-3">
                  <p className="text-lg font-medium">🛒 Tu carrito está vacío</p>
                  <button
                    onClick={closeCart}
                    className="mt-3 px-6 py-2 bg-black text-white rounded-full hover:bg-[#d24e1e] transition font-semibold text-sm"
                  >
                    Seguir comprando
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={`${item.id}-${item.pack || ""}`}
                    className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition bg-white"
                  >
                    {/* Imagen principal */}
                    <div className="w-full aspect-[4/3] bg-gray-100 relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white text-red-500 rounded-full p-1 shadow-sm transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Info del producto */}
                    <div className="p-3 flex flex-col gap-1">
                      <h3 className="font-semibold text-[14px] leading-tight text-gray-800 line-clamp-2">
                        {getPackText(item)}
                      </h3>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wide">
                        {item.pack}
                      </p>

                      <p className="text-sm font-bold text-gray-900 mt-1">
                        Total: ₡{Number(item.total).toLocaleString()}
                      </p>

                      {/* Cantidad */}
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                Math.max(Number(item.quantity) - 1, 1)
                              )
                            }
                            className="border rounded w-6 h-6 flex items-center justify-center hover:bg-gray-100"
                          >
                            <Minus size={10} />
                          </button>
                          <span className="text-[13px] font-medium w-5 text-center">
                            {Number(item.quantity) || 1}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.id, Number(item.quantity) + 1)
                            }
                            className="border rounded w-6 h-6 flex items-center justify-center hover:bg-gray-100"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer fijo */}
            {cart.length > 0 && (
              <div className="p-5 border-t bg-white sticky bottom-0 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
                {(() => {
                  const totalUnits = cart.reduce((acc, item) => {
                    const packMatch = item.pack?.match(/\d+/);
                    const packQuantity = packMatch ? parseInt(packMatch[0]) : 1;
                    return acc + packQuantity * (item.quantity || 1);
                  }, 0);

                  const isSingleOnePack =
                    cart.length === 1 &&
                    (cart[0].pack?.toLowerCase().includes("1 pack") ||
                      cart[0].pack === "1" ||
                      cart[0].pack === "Pack 1" ||
                      cart[0].pack === "1 unidad");

                  const shippingCost =
                    isSingleOnePack && totalUnits < 2 ? 2500 : 0;
                  const totalWithShipping = Number(totalPrice) + shippingCost;

                  return (
                    <>
                      <div className="mb-2">
                        {shippingCost > 0 ? (
                          <div className="flex justify-between text-sm text-gray-700">
                            <span>Costo de envío:</span>
                            <span className="font-semibold text-gray-900">
                              ₡{shippingCost.toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-between text-sm text-gray-700">
                            <span>Costo de envío:</span>
                            <span>
                              <span className="line-through opacity-60 mr-1">
                                ₡2,500
                              </span>
                              <span className="font-semibold text-green-600">
                                GRATIS
                              </span>
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between mb-4">
                        <span className="text-gray-700 font-medium text-lg">
                          Total general:
                        </span>
                        <span className="text-xl font-bold">
                          ₡{totalWithShipping.toLocaleString()}
                        </span>
                      </div>

                      <p className="text-xs text-gray-500 text-center italic">
                        🚚 Envío gratis a partir de más de 1 unidad
                      </p>

                      <button
                        onClick={() => {
                          closeCart();
                          navigate("/checkout");
                        }}
                        className="w-full bg-black text-white font-semibold py-3 rounded-lg hover:bg-[#d24e1e] transition"
                      >
                        FINALIZAR COMPRA
                      </button>

                      <button
                        onClick={closeCart}
                        className="w-full mt-3 text-sm text-gray-500 hover:text-black transition"
                      >
                        Seguir comprando
                      </button>
                    </>
                  );
                })()}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
